import type {Course} from '@goschool/model'
import {Avatar, Card, CardActionArea, CardActions, CardHeader, Skeleton, Stack, styled, Typography} from '@mui/material'
import {GoSchool} from '@goschool/routing'
import {Link as RouterLink} from 'react-router-dom'
import {useCourseContext} from '@goschool/dao'
import {InstructorActionLink, StudentActionLink} from './CourseActions'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import SettingsIcon from '@mui/icons-material/Settings'
import {useTranslation} from 'react-i18next'
import {useMemo} from 'react'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import {getActiveCourseAccessDisabledWindow, isCourseArchived} from '@goschool/model'


export function CourseCard() {
  const {courseSnapshot} = useCourseContext()
  const course = useMemo(() => courseSnapshot.data(), [courseSnapshot])
  const {t} = useTranslation()
  const activeDisabledWindow = useMemo(
    () => getActiveCourseAccessDisabledWindow(course),
    [course]
  )

  const instructorNames = useMemo(
    () => {
      return course.instructors.map(instructor => instructor.name).join(', ')
    },
    [course]
  )

  return <StyledCourseCard variant="elevation">
    <CardActionArea
      component={RouterLink}
      to={GoSchool.courseChat(courseSnapshot.ref)}>
      <CardHeader avatar={<CourseAvatar course={course}/>} title={course.title}
        subheader={<Stack spacing={0.25} minWidth={0}>
          <CourseStatusText
            archived={isCourseArchived(course)}
            temporarilyClosed={activeDisabledWindow != null}
            archivedLabel={t('course:status.archived')}
            temporarilyClosedLabel={t('course:status.temporarilyClosed')}
          />
          {instructorNames ?? <Skeleton/>}
        </Stack>}
      />
    </CardActionArea>

    <CardActions
      disableSpacing><CourseActions/></CardActions>
  </StyledCourseCard>
}


function CourseActions() {
  const {courseSnapshot, courseRoles} = useCourseContext()
  const isInstructor = useMemo(
    () => courseRoles.includes('instructor') || courseRoles.includes('admin'),
    [courseRoles]
  )
  const {t} = useTranslation()
  if (isInstructor) {
    return <>
      <InstructorActionLink
        color="inherit" title={t('course:actions.studentChats')} edge="end"
        to={GoSchool.courseChats(courseSnapshot.ref)}
        Icon={ForumOutlinedIcon}/>
      <InstructorActionLink
        color="inherit" title={t('course:actions.documents')}
        to={GoSchool.courseDocuments(courseSnapshot.ref)}
        Icon={MenuBookIcon}/>
      <InstructorActionLink
        color="inherit" title={t('course:actions.details')} to={GoSchool.course(courseSnapshot.ref)}
        Icon={SettingsIcon}/>
    </>
  } else {
    return <>
      <StudentActionLink
        color="inherit" title={t('course:actions.documents')} edge="end"
        to={GoSchool.courseDocuments(courseSnapshot.ref)}
        Icon={MenuBookIcon}/>
      <StudentActionLink
        color="inherit" title={t('course:actions.details')} to={GoSchool.course(courseSnapshot.ref)}
        Icon={SettingsIcon}/>
    </>
  }
}

function CourseStatusText({
  archived,
  temporarilyClosed,
  archivedLabel,
  temporarilyClosedLabel
}: {
  archived: boolean
  temporarilyClosed: boolean
  archivedLabel: string
  temporarilyClosedLabel: string
}) {
  if (!archived && !temporarilyClosed) {
    return null
  }

  return <Stack direction="row" gap={1} flexWrap="wrap" minWidth={0}>
    {archived &&
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {archivedLabel}
      </Typography>}
    {temporarilyClosed &&
      <Typography variant="caption" color="warning.dark" fontWeight={700}>
        {temporarilyClosedLabel}
      </Typography>}
  </Stack>
}

function CourseAvatar({course}: { course: Course }) {
  if (course.icon != null) {
    return <Avatar src={course.icon}/>
  } else if (course.banner != null) {
    return <Avatar src={course.banner}/>
  } else {
    return <Avatar>{course.title.slice(0, 2).toUpperCase()}</Avatar>
  }
}

const StyledCourseCard = styled(Card, {name: 'StyledCourseCard', slot: 'Root'})(
  ({theme}) => ({
    '& .MuiCardHeader-root': {
      height: '6em',
      '& .MuiAvatar-root': {
        width: theme.spacing(8),
        height: theme.spacing(8)
      }
    },
    '& .MuiCardActions-root': {
      borderTop: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1),
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'stretch'
    }
  })
)
